import { useState } from "react";
import { useParams, Link } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import {
  ArrowLeft,
  ChevronLeft,
  ChevronRight,
  Monitor,
  Smartphone,
  Book,
  Volume2,
} from "lucide-react";
import { Button, Spinner } from "@learn-me/ui";
import { adminApi, type CoursePreview } from "@/features/admin/api/adminApi";
import { InteractiveContent, AudioPlayer } from "@/components/content";

export default function CoursePreviewPage() {
  const { courseId } = useParams<{ courseId: string }>();
  const [currentChapterIndex, setCurrentChapterIndex] = useState(0);
  const [currentSectionIndex, setCurrentSectionIndex] = useState(0);
  const [deviceMode, setDeviceMode] = useState<"desktop" | "mobile">("desktop");
  const [sidebarOpen, setSidebarOpen] = useState(true);

  // Fetch course with all chapters and sections
  const {
    data: course,
    isLoading,
    error,
  } = useQuery<CoursePreview>({
    queryKey: ["course-preview", courseId],
    queryFn: () => adminApi.getCoursePreview(courseId!),
    enabled: !!courseId,
  });

  if (isLoading) {
    return (
      <div className="h-screen flex items-center justify-center bg-gray-100">
        <Spinner className="h-8 w-8" />
      </div>
    );
  }

  if (error || !course) {
    return (
      <div className="h-screen flex items-center justify-center bg-gray-100">
        <div className="text-center">
          <p className="text-red-500 mb-4">Error al cargar el curso</p>
          <Link to={`/admin/courses/${courseId}`}>
            <Button variant="outline">
              <ArrowLeft className="h-4 w-4 mr-2" />
              Volver al Editor
            </Button>
          </Link>
        </div>
      </div>
    );
  }

  const currentChapter = course.chapters[currentChapterIndex];
  const hasSections = currentChapter && currentChapter.sections.length > 0;
  const currentSection = hasSections
    ? currentChapter.sections[currentSectionIndex]
    : null;

  // Navigation helpers
  const totalChapters = course.chapters.length;
  const totalSections = currentChapter?.sections.length || 0;

  const canGoPrevChapter = currentChapterIndex > 0;
  const canGoNextChapter = currentChapterIndex < totalChapters - 1;
  const canGoPrevSection = currentSectionIndex > 0;
  const canGoNextSection = currentSectionIndex < totalSections - 1;

  const goToPrevSection = () => {
    if (canGoPrevSection) {
      setCurrentSectionIndex((prev) => prev - 1);
    } else if (canGoPrevChapter) {
      setCurrentChapterIndex((prev) => prev - 1);
      const prevChapter = course.chapters[currentChapterIndex - 1];
      setCurrentSectionIndex(
        prevChapter?.sections.length ? prevChapter.sections.length - 1 : 0,
      );
    }
  };

  const goToNextSection = () => {
    if (canGoNextSection) {
      setCurrentSectionIndex((prev) => prev + 1);
    } else if (canGoNextChapter) {
      setCurrentChapterIndex((prev) => prev + 1);
      setCurrentSectionIndex(0);
    }
  };

  const selectChapter = (index: number) => {
    setCurrentChapterIndex(index);
    setCurrentSectionIndex(0);
  };

  const selectSection = (chapterIndex: number, sectionIndex: number) => {
    setCurrentChapterIndex(chapterIndex);
    setCurrentSectionIndex(sectionIndex);
  };

  // Get content to display
  const contentToShow =
    currentSection?.content || currentChapter?.content || "";
  const audioUrl = currentSection?.audioUrl || currentChapter?.audioUrl;
  const audioDuration =
    currentSection?.audioDuration || currentChapter?.audioDuration;
  const contentTitle = currentSection?.title || currentChapter?.title || "";

  return (
    <div className="h-screen flex flex-col bg-gray-100">
      {/* Header */}
      <header className="bg-white border-b px-4 py-3 flex items-center justify-between">
        <div className="flex items-center gap-4">
          <Link to={`/admin/courses/${courseId}`}>
            <Button variant="ghost" size="sm">
              <ArrowLeft className="h-4 w-4 mr-2" />
              Volver al Editor
            </Button>
          </Link>
          <div className="border-l pl-4">
            <h1 className="font-semibold text-gray-900 truncate max-w-md">
              {course.title}
            </h1>
            <p className="text-xs text-gray-500">Vista Previa del Curso</p>
          </div>
        </div>

        {/* Chapter navigation */}
        <div className="flex items-center gap-2">
          <Button
            variant="ghost"
            size="sm"
            onClick={() => {
              if (canGoPrevChapter) {
                setCurrentChapterIndex((prev) => prev - 1);
                setCurrentSectionIndex(0);
              }
            }}
            disabled={!canGoPrevChapter}
          >
            <ChevronLeft className="h-4 w-4" />
          </Button>
          <span className="text-sm text-gray-600 min-w-[120px] text-center">
            Capitulo {currentChapterIndex + 1} de {totalChapters}
          </span>
          <Button
            variant="ghost"
            size="sm"
            onClick={() => {
              if (canGoNextChapter) {
                setCurrentChapterIndex((prev) => prev + 1);
                setCurrentSectionIndex(0);
              }
            }}
            disabled={!canGoNextChapter}
          >
            <ChevronRight className="h-4 w-4" />
          </Button>
        </div>

        {/* Device toggle */}
        <div className="flex items-center gap-2">
          <button
            type="button"
            onClick={() => setSidebarOpen(!sidebarOpen)}
            className="p-2 text-gray-500 hover:text-gray-700 hover:bg-gray-100 rounded-lg"
            title="Toggle sidebar"
          >
            <Book className="h-5 w-5" />
          </button>
          <div className="flex items-center bg-gray-100 rounded-lg p-0.5">
            <button
              type="button"
              onClick={() => setDeviceMode("desktop")}
              className={`p-2 rounded-md transition-colors ${
                deviceMode === "desktop"
                  ? "bg-white shadow-sm text-gray-900"
                  : "text-gray-500 hover:text-gray-700"
              }`}
              title="Vista escritorio"
            >
              <Monitor className="h-4 w-4" />
            </button>
            <button
              type="button"
              onClick={() => setDeviceMode("mobile")}
              className={`p-2 rounded-md transition-colors ${
                deviceMode === "mobile"
                  ? "bg-white shadow-sm text-gray-900"
                  : "text-gray-500 hover:text-gray-700"
              }`}
              title="Vista movil"
            >
              <Smartphone className="h-4 w-4" />
            </button>
          </div>
        </div>
      </header>

      {/* Main content area */}
      <div className="flex-1 flex overflow-hidden">
        {/* Sidebar - Course outline */}
        {sidebarOpen && (
          <aside className="w-72 bg-white border-r overflow-y-auto">
            <div className="p-4">
              <h3 className="font-semibold text-gray-900 mb-4">
                Contenido del Curso
              </h3>
              <div className="space-y-2">
                {course.chapters.map((chapter, chIdx) => (
                  <div key={chapter.id}>
                    <button
                      type="button"
                      onClick={() => selectChapter(chIdx)}
                      className={`w-full text-left px-3 py-2 rounded-lg text-sm transition-colors ${
                        chIdx === currentChapterIndex
                          ? "bg-primary-50 text-primary-700 font-medium"
                          : "text-gray-700 hover:bg-gray-50"
                      }`}
                    >
                      <div className="flex items-center gap-2">
                        <span className="flex-shrink-0 w-5 h-5 rounded-full bg-gray-200 text-xs flex items-center justify-center">
                          {chIdx + 1}
                        </span>
                        <span className="truncate">{chapter.title}</span>
                      </div>
                    </button>

                    {/* Sections within chapter */}
                    {chIdx === currentChapterIndex &&
                      chapter.sections.length > 0 && (
                        <div className="ml-6 mt-1 space-y-1">
                          {chapter.sections.map((section, sIdx) => (
                            <button
                              key={section.id}
                              type="button"
                              onClick={() => selectSection(chIdx, sIdx)}
                              className={`w-full text-left px-3 py-1.5 rounded text-xs transition-colors flex items-center gap-2 ${
                                sIdx === currentSectionIndex
                                  ? "text-primary-600 bg-primary-50"
                                  : "text-gray-500 hover:text-gray-700 hover:bg-gray-50"
                              }`}
                            >
                              {section.audioUrl && (
                                <Volume2 className="h-3 w-3 flex-shrink-0" />
                              )}
                              <span className="truncate">{section.title}</span>
                            </button>
                          ))}
                        </div>
                      )}
                  </div>
                ))}
              </div>
            </div>
          </aside>
        )}

        {/* Preview content */}
        <main className="flex-1 overflow-y-auto p-6 flex items-start justify-center">
          <div
            className={`bg-white rounded-xl shadow-lg overflow-hidden transition-all ${
              deviceMode === "mobile" ? "w-[375px]" : "w-full max-w-4xl"
            }`}
          >
            {/* Section indicators (dots) - only show if there are sections */}
            {hasSections && totalSections > 1 && (
              <div className="flex justify-center gap-2 p-4 bg-gray-50 border-b">
                {currentChapter?.sections.map((_, idx) => (
                  <button
                    key={idx}
                    type="button"
                    onClick={() => setCurrentSectionIndex(idx)}
                    className={`w-2.5 h-2.5 rounded-full transition-colors ${
                      idx === currentSectionIndex
                        ? "bg-primary-600"
                        : "bg-gray-300 hover:bg-gray-400"
                    }`}
                  />
                ))}
              </div>
            )}

            {/* Content */}
            <div className="p-6">
              {/* Title */}
              <h2 className="text-2xl font-bold text-gray-900 mb-4">
                {contentTitle}
              </h2>

              {/* Audio player */}
              {audioUrl && (
                <div className="mb-6">
                  <AudioPlayer
                    audioUrl={audioUrl}
                    audioDuration={audioDuration ?? undefined}
                    title={contentTitle}
                  />
                </div>
              )}

              {/* Content */}
              {contentToShow ? (
                <InteractiveContent html={contentToShow} defaultExpanded />
              ) : (
                <div className="text-center py-12 text-gray-500">
                  <p>Este capitulo no tiene contenido</p>
                </div>
              )}
            </div>

            {/* Section navigation */}
            {(hasSections || totalChapters > 1) && (
              <div className="flex justify-between items-center p-4 border-t bg-gray-50">
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={goToPrevSection}
                  disabled={!canGoPrevSection && !canGoPrevChapter}
                >
                  <ChevronLeft className="h-4 w-4 mr-1" />
                  {canGoPrevSection ? "Seccion Anterior" : "Capitulo Anterior"}
                </Button>
                <span className="text-xs text-gray-500">
                  {hasSections
                    ? `Seccion ${currentSectionIndex + 1} de ${totalSections}`
                    : "Sin secciones"}
                </span>
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={goToNextSection}
                  disabled={!canGoNextSection && !canGoNextChapter}
                >
                  {canGoNextSection
                    ? "Siguiente Seccion"
                    : "Siguiente Capitulo"}
                  <ChevronRight className="h-4 w-4 ml-1" />
                </Button>
              </div>
            )}
          </div>
        </main>
      </div>

      {/* Footer */}
      <footer className="bg-white border-t px-4 py-2 text-center text-xs text-gray-500">
        Vista previa - Asi veran los estudiantes el contenido del curso
      </footer>
    </div>
  );
}
